import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Lightbulb, CheckCircle, Copy, Download } from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const ReteachPack = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTopic = searchParams.get("topic") || "";
  
  const [topic, setTopic] = useState(initialTopic);
  const [questionCount, setQuestionCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState(null);

  const getTeacherToken = () => localStorage.getItem("teacherToken");

  const generatePack = async () => {
    if (!topic.trim()) {
      alert("Please enter a topic");
      return;
    }

    setLoading(true);
    try {
      const token = getTeacherToken();
      if (!token) {
        navigate("/teacher/login");
        return;
      }

      const res = await fetch(`${API_URL}/teacher/reteach-pack`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          topic: topic.trim(),
          question_count: questionCount,
          difficulty: "easy"
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPack(data);
      } else {
        alert("Failed to generate reteach pack");
      }
    } catch (error) {
      console.error("Error generating pack:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!pack) return;
    const text = pack.questions.map((q, i) => (
      `${i + 1}. ${q.question}\n` +
      q.options.map((opt, j) => `   ${String.fromCharCode(65 + j)}. ${opt}`).join("\n") +
      `\n   Answer: ${String.fromCharCode(65 + q.correct_answer)}\n   Explanation: ${q.explanation}\n`
    )).join("\n");
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  // Common struggling topics for quick selection
  const quickTopics = [
    "Fractions", "Decimals", "Word Problems", "Reading Comprehension",
    "Main Idea", "Algebra", "Ratios", "Grammar", "Vocabulary", "Scientific Method"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/teacher/dashboard" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl">Reteach Pack Generator</h1>
              <p className="text-orange-100">Create remediation question sets for struggling topics</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!pack ? (
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-6">
            {/* Topic Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Topic to Reteach</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-0 text-lg"
                placeholder="e.g., Fractions, Word Problems, Reading Comprehension..."
              />
            </div>

            {/* Quick Topic Selection */}
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-2">Quick select common topics:</p>
              <div className="flex flex-wrap gap-2">
                {quickTopics.map(t => (
                  <button
                    key={t}
                    onClick={() => setTopic(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      topic === t
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Count */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Number of Questions</label>
              <div className="flex gap-2">
                {[5, 10, 15, 20].map(num => (
                  <button
                    key={num}
                    onClick={() => setQuestionCount(num)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      questionCount === num
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-800">How Reteach Packs Work</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Questions are generated at easy-to-medium difficulty to build foundational understanding. 
                    Each question includes a clear explanation to help students learn from their mistakes.
                  </p>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generatePack}
              disabled={loading || !topic.trim()}
              className="w-full py-4 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating Questions...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Generate Reteach Pack
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pack Header */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <h2 className="font-heading font-bold text-xl text-slate-800">Reteach Pack Ready!</h2>
                  </div>
                  <p className="text-slate-600 mt-1">
                    <span className="font-medium">{pack.topic}</span> • {pack.question_count} questions
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy All
                  </button>
                  <button
                    onClick={() => setPack(null)}
                    className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
                  >
                    New Pack
                  </button>
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {pack.questions.map((question, i) => (
                <div key={i} className="bg-white rounded-xl border-2 border-slate-200 p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-600">
                      {i + 1}
                    </div>
                    <p className="font-medium text-slate-800 flex-1">{question.question}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mb-4 ml-11">
                    {question.options.map((option, j) => (
                      <div
                        key={j}
                        className={`p-3 rounded-lg ${
                          j === question.correct_answer
                            ? "bg-green-50 border-2 border-green-300"
                            : "bg-slate-50 border border-slate-200"
                        }`}
                      >
                        <span className={`font-medium ${j === question.correct_answer ? "text-green-700" : "text-slate-600"}`}>
                          {String.fromCharCode(65 + j)}.
                        </span>{" "}
                        <span className={j === question.correct_answer ? "text-green-800" : "text-slate-700"}>
                          {option}
                        </span>
                        {j === question.correct_answer && (
                          <CheckCircle className="w-4 h-4 text-green-500 inline ml-2" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="ml-11 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Explanation:</span> {question.explanation}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => setPack(null)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200"
              >
                Create Another Pack
              </button>
              <Link
                to="/teacher/assignments/new"
                className="flex-1 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 text-center"
              >
                Create Assignment with These
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReteachPack;
